import { ReactNode } from "react";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";

import { PlusIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useListUsersUsersGetQuery } from "api";
import { can } from "authorization/authorization.util";
import { UserPermissionEnum } from "authorization/types";
import { DynamicButton } from "components/DynamicButton/DynamicButton";
import { ErrorPage, LoadingPage, NoDataPage } from "components/pages";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { computeZones } from "hooks/useGetZones";
import { IZone } from "interfaces/zone.interface";

import { UserList } from "./components/UserList";

import { PageHeader } from "../components/PageHeader";
import { useListStore } from "../stores/listStore";

const FULL_ACCESS_KEY = "usersFullAccess";

type WrapperProps = {
  children: ReactNode;
  title: string;
  zoneId: string;
};

function Wrapper({ children, title, zoneId }: WrapperProps) {
  const { t } = useTranslation();
  const { permissions } = useAuthenticatedUser();

  const canCreateUser = can(permissions, UserPermissionEnum["user.create"]);

  const selected = useListStore((s) => s.selections[FULL_ACCESS_KEY] ?? false);
  const setSelection = useListStore((s) => s.setSelection);

  return (
    <Stack sx={{ minHeight: "100vh", gap: 2 }}>
      <PageHeader title={title}>
        <Stack direction={"row"} sx={{ alignItems: "center", flexGrow: 1 }}>
          {canCreateUser && (
            <DynamicButton
              href={`/administration/zones/${zoneId}/users/create`}
              startIcon={<PlusIcon />}
              variant={"text"}
            >
              {t("create_employee")}
            </DynamicButton>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <FormControlLabel
            control={
              <Checkbox
                checked={selected}
                onChange={(_, checked) =>
                  setSelection(FULL_ACCESS_KEY, checked)
                }
              />
            }
            label={t("show_only_users_with_full_zone_access")}
          />
        </Stack>
      </PageHeader>
      {children}
    </Stack>
  );
}

type UserListPageProps = {
  zone: IZone;
  zones: IZone[];
};

export const UserListPage = ({ zone, zones }: UserListPageProps) => {
  const { t } = useTranslation();

  const fullAccess = useListStore(
    (s) => s.selections[FULL_ACCESS_KEY] ?? false,
  );

  const {
    data: userData,
    isLoading,
    isError,
    refetch,
  } = useListUsersUsersGetQuery({
    embed: ["zones"],
  });

  const userList = (userData?.data ?? [])
    .map((user) => ({
      ...user,
      zones: {
        data: computeZones(zones, user.zones?.data),
      },
    }))
    .filter((user) =>
      user.zones.data.some(
        (z) =>
          z.id === zone.id &&
          ((z.hasPartialAccess && !fullAccess) || z.hasAccess),
      ),
    );

  if (isLoading) {
    return (
      <Wrapper title={zone.name} zoneId={zone.id}>
        <LoadingPage />
      </Wrapper>
    );
  }

  if (isError) {
    return (
      <Wrapper title={zone.name} zoneId={zone.id}>
        <ErrorPage
          title={t("Failed to load users")}
          buttonOnClick={() => refetch()}
          buttonText={t("Try again")}
        />
      </Wrapper>
    );
  }

  if (userList.length === 0) {
    return (
      <Wrapper title={zone.name} zoneId={zone.id}>
        <NoDataPage
          message={t("there_are_no_employees_in_this_zone")}
          title={zone.name}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper title={zone.name} zoneId={zone.id}>
      <UserList users={userList} zoneId={zone.id} />
    </Wrapper>
  );
};
